import { useSelector } from 'react-redux'
import type {
  KycProvider,
  KycVerificationStatus,
  UserRoleName,
} from 'src/generated'
import {
  currentProviderFeatures,
  userRoleSelector,
} from 'src/redux/providers/selectors'
import type { ProviderFeatureFlag, RouteAccessRule } from './routeAccess'
import { routeAccessAllowed } from './routeAccess'

// ----------------------------------------------------------------------
// Who may decide a verification the KYC provider held back for a person.
//
// Didit puts a licence or face check it cannot decide `In Review` and leaves
// it to Beast. Beast staff decide any of them. A fleet's admin or fleet
// manager decides the ones sent to the fleet, once Beast has given the fleet
// "Decide licence reviews" (`kycReviewEnabled`): asked for on 2026-10-02, so
// that a fleet can approve or decline in BeastOS instead of waiting on Beast.
//
// `kycReviews`, `kycReview` and `kycReviewDecide` hold the same rule on the
// server (`@auth`, `@feature`, and the review's own fleets); this is the rule
// for the nav and the route. docs/KYC_PROVIDERS.md.
// ----------------------------------------------------------------------

export const KYC_REVIEW_ROLES: readonly UserRoleName[] = [
  'superAdmin',
  'admin',
  'fleetManager',
]

export const KYC_REVIEW_ACCESS = {
  requiredFeatureFlag: 'kycReviewEnabled',
  requiredRole: KYC_REVIEW_ROLES,
} as const satisfies RouteAccessRule

/** Whether this viewer may open the review queue and the reviews in it. */
export const kycReviewsVisibleTo = (
  role: UserRoleName | null | undefined,
  features: readonly ProviderFeatureFlag[],
): boolean => routeAccessAllowed(KYC_REVIEW_ACCESS, role, features)

export const useKycReviewsVisible = (): boolean =>
  kycReviewsVisibleTo(
    useSelector(userRoleSelector),
    useSelector(currentProviderFeatures),
  )

/**
 * Whether this viewer sees every review, not only the ones sent to a fleet:
 * Beast staff. A customer's Verification tab links its waiting review only for
 * them, because a fleet the review was not sent to would open a page that is
 * not found.
 */
export const kycReviewsAllVisibleTo = (
  role: UserRoleName | null | undefined,
): boolean => role === 'superAdmin'

export const useKycReviewsAllVisible = (): boolean =>
  kycReviewsAllVisibleTo(useSelector(userRoleSelector))

/**
 * Whether a verification waits for a person: in review, with the provider
 * that leaves its reviews to Beast (Didit). Veriff's staff decided Veriff's
 * own (`KYC_PROVIDER_REVIEWS_ITSELF` on the server).
 */
export const kycVerificationAwaitsBeast = (verification: {
  status: KycVerificationStatus
  provider: KycProvider
}): boolean =>
  verification.status === 'review' && verification.provider === 'didit'

/** Where a held verification is decided. */
export const kycReviewPath = (verificationId: number): string =>
  `/admin/misc/verification-reviews/${String(verificationId)}`
